import { Menu } from 'lucide-react';
import { useCallback, useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { NavLink, useRouteLoaderData } from 'react-router';
import { useUser } from '~/hooks/user';
import { isCreatorLike } from '~/lib/permissions';
import { useHasPermission } from '~/contexts/permissions-context';
import { cn } from '~/lib/utils';
import { useNotificationsSheet } from '~/hooks/use-notifications-sheet';
import { Button, buttonVariants } from './button';
import { GamificationPills } from '~/components/ui/gamification/gamification-metrics';
import { NavbarMenu } from './navbar-menu';
import { NotificationButton } from './notification-button';
import { NotificationSheet } from './notification-sheet';
import { NavbarLanguageSwitcher } from './navbar-language-switcher';
import { Sheet, SheetTrigger, SheetContent } from './sheet';
import { $path } from 'safe-routes';
import { t } from '@transifex/native';
import type { components } from '~/api/openapi';
import type { loader } from '~/routes/learner-app-layout';

type NavbarProps = HTMLAttributes<HTMLDivElement> & {
  leftSlot?: ReactNode;
  middleSlot?: ReactNode;
  rightSlot?: ReactNode;
  bottomSlot?: ReactNode;
};

export function Navbar({
  leftSlot,
  middleSlot,
  rightSlot,
  bottomSlot,
  className,
  ...props
}: NavbarProps) {
  return (
    <header className={cn('sticky top-0 z-50 bg-background-0 shadow-sm', className)} {...props}>
      <div
        className={cn(
          `
            xs:gap-xs
            grid min-h-17 grid-cols-2 px-lg py-sm
            md:gap-md
          `,
          middleSlot ? 'md:grid-cols-3' : 'md:grid-cols-[1fr_minmax(0,max-content)]'
        )}
      >
        {leftSlot && (
          <div
            className="
              col-start-1 row-start-1 flex items-center justify-start gap-md
            "
          >
            {leftSlot}
          </div>
        )}
        {middleSlot && (
          <div
            className="
              col-span-2 col-start-1 row-start-2 flex items-center
              justify-center gap-md
              md:col-span-1 md:col-start-2 md:row-start-1
            "
          >
            {middleSlot}
          </div>
        )}
        <div
          className={cn(
            'col-start-2 row-start-1 flex items-center justify-end gap-md',
            middleSlot ? 'md:col-start-3' : 'md:col-start-2'
          )}
        >
          {rightSlot}
          <GamificationPills
            className="
              hidden
              md:flex
            "
          />
          <ControlCenter />
        </div>
      </div>
      {bottomSlot}
    </header>
  );
}

function Logo({ className, ...props }: HTMLAttributes<HTMLAnchorElement>) {
  return (
    <NavLink to="/" className={className} {...props}>
      <img alt="Startable logo" src="/favicon/favicon.svg" className="size-10" />
      <span className="sr-only">Startable</span>
    </NavLink>
  );
}

type CommonNavigationItem = {
  name: string;
  to: string;
  onClick?: () => void;
};

const generateCommonNavigationItems = (user?: components['schemas']['MeResponseDto'] | null) => {
  const items: CommonNavigationItem[] = [];

  if (!user) {
    return items;
  }

  const creatorLike = isCreatorLike(user?.accountType);

  items.push({
    name: t('Journeys'),
    to: $path(creatorLike ? '/creator/journeys' : '/journeys'),
  });

  // enable the glossary menu for creator temporarily
  if (creatorLike) {
    items.push({
      name: t('Glossary'),
      to: $path(creatorLike ? '/creator/glossary' : '/glossary'),
    });
  }

  return items;
};

export function NavigationItem(props: {
  className?: string;
  name: string;
  to: string;
  onClick?: () => void;
  badge?: ReactNode;
}) {
  return (
    <NavLink
      to={props.to}
      className={({ isActive }) =>
        cn(
          buttonVariants({ variant: isActive ? 'active' : 'ghost' }),
          'rounded-sm p-2',
          !isActive && 'font-normal',
          props.className
        )
      }
      onClick={props.onClick}
    >
      <div className="relative flex items-center gap-sm">
        {props.name}
        {props.badge}
      </div>
    </NavLink>
  );
}

export function CommonNavigation() {
  const user = useUser();
  const navigationItems = generateCommonNavigationItems(user);
  const canViewLearners = useHasPermission('learner:all');

  const creatorLike = isCreatorLike(user?.accountType);
  if (canViewLearners) {
    navigationItems.push({
      name: t('Learners'),
      to: $path(creatorLike ? '/creator/learners' : '/learners'),
    });
  }
  const hasNavigationItems = navigationItems.length > 0;

  return (
    <>
      <nav
        className="
          hidden items-center gap-sm
          md:flex
        "
      >
        <Logo className="mr-sm shrink-0" />
        {navigationItems.map(navItem => (
          <NavigationItem key={navItem.name + navItem.to} {...navItem} />
        ))}
      </nav>
      {hasNavigationItems && (
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="
                size-10 rounded-sm bg-background-100 p-sm!
                md:hidden
              "
            >
              <Menu className="size-8 text-text-900" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left">
            <div className="grid gap-4 p-4">
              {navigationItems.map(navItem => (
                <NavigationItem
                  key={navItem.name + navItem.to}
                  {...navItem}
                  className="justify-start"
                />
              ))}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

export function ControlCenter() {
  const user = useUser();
  const learnerAppData = useRouteLoaderData<typeof loader>('learner-app-layout');
  const {
    isOpen: isNotificationsOpen,
    toggle: toggleNotifications,
    close: closeNotifications,
  } = useNotificationsSheet();
  const [unseenCountOverride, setUnseenCountOverride] = useState<Promise<number> | null>(null);

  // Clear the override when the layout loader revalidates (e.g. after navigation
  // or after the mark-seen action completes), so new notifications show correctly.
  useEffect(() => {
    setUnseenCountOverride(null);
  }, [learnerAppData?.unseenCountPromise]);

  const handleNotificationClick = () => {
    toggleNotifications();
    // TODO: Add to Amplitude tracking plan
    // ampli.clickNotificationsTopNavigation();
  };

  const handleMarkAllAsRead = useCallback(() => {
    setUnseenCountOverride(Promise.resolve(0));
  }, []);

  return (
    <>
      <div className="flex items-center gap-md">
        {/* Show navbar switcher only for logged-out users; logged-in users access it via the user menu dropdown */}
        {!user && <NavbarLanguageSwitcher />}
        {user?.accountType === 'LEARNER' && learnerAppData && (
          <NotificationButton
            unseenCountPromise={unseenCountOverride ?? learnerAppData.unseenCountPromise}
            onClick={handleNotificationClick}
            isActive={isNotificationsOpen}
          />
        )}
        <NavbarMenu />
      </div>
      {user?.accountType === 'LEARNER' && (
        <NotificationSheet
          isOpen={isNotificationsOpen}
          onClose={closeNotifications}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      )}
    </>
  );
}
